import { OneOnOnesTeamEmployeeFragment } from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { formatDateToString, formatTimeToString } from './dates'

export type MeetingInfo = {
  text: string
  status: 'positive' | 'neutral' | 'info'
  meetingId?: string | null
  startDate: string
  endDate: string
  templateID?: string | null
}

export type MeetingEmployee = OneOnOnesTeamEmployeeFragment & {
  directReport: boolean
  isManager: boolean
  isDirectManager: boolean
  nextMeeting: MeetingInfo | null
}

interface ScheduledMeeting {
  startTime: string
  endTime: string
  id?: string | null
  template?: {
    id: string
  } | null
}

const MEETING_PRIORITIES = {
  IN_PROGRESS: 1,
  FUTURE_TODAY: 2,
  PAST_TODAY: 3,
  FUTURE: 4,
  PAST: 5,
  NONE: 6,
} as const

const getMeetingPriority = (meeting: MeetingInfo | null, now: moment.Moment): number => {
  if (!meeting) return MEETING_PRIORITIES.NONE

  const startMoment = moment(meeting.startDate)
  const endMoment = moment(meeting.endDate)
  const today = now.clone().startOf('day')

  const isInProgress = now.isBetween(startMoment, endMoment)
  const isToday = startMoment.isSame(today, 'day')
  const isPastToday = endMoment.isBefore(now) && isToday
  const isFutureToday = startMoment.isAfter(now) && isToday
  const isFuture = startMoment.isAfter(now) && !isToday
  const isPast = endMoment.isBefore(now) && !isToday

  if (isInProgress) return MEETING_PRIORITIES.IN_PROGRESS
  if (isFutureToday) return MEETING_PRIORITIES.FUTURE_TODAY
  if (isPastToday) return MEETING_PRIORITIES.PAST_TODAY
  if (isFuture) return MEETING_PRIORITIES.FUTURE
  if (isPast) return MEETING_PRIORITIES.PAST
  return MEETING_PRIORITIES.NONE
}

const getTimeComparison = (aMeeting: MeetingInfo, bMeeting: MeetingInfo, priority: number) => {
  const aStart = moment(aMeeting.startDate)
  const aEnd = moment(aMeeting.endDate)
  const bStart = moment(bMeeting.startDate)
  const bEnd = moment(bMeeting.endDate)

  // For past meetings, sort by end time (most recent first)
  if (priority === MEETING_PRIORITIES.PAST_TODAY || priority === MEETING_PRIORITIES.PAST) {
    return bEnd.diff(aEnd)
  }

  // For all other meetings, sort by start time (sooner first)
  return aStart.diff(bStart)
}

/**
 * Sorts employees by meeting priority and time
 * Priority order:
 * 1. Meetings happening now
 * 2. Meetings happening today (future)
 * 3. Meetings that happened today (past)
 * 4. Future meetings
 * 5. Past meetings
 * 6. No meetings
 */
export const sortEmployeesByMeetingPriority = (
  employees: MeetingEmployee[],
  locale: string
): MeetingEmployee[] => {
  const now = moment().locale(locale)

  return employees.sort((a, b) => {
    const aPriority = getMeetingPriority(a.nextMeeting, now)
    const bPriority = getMeetingPriority(b.nextMeeting, now)

    // Different priorities: sort by priority
    if (aPriority !== bPriority) {
      return aPriority - bPriority
    }

    // Same priority: sort by meeting time
    if (!a.nextMeeting || !b.nextMeeting) {
      return 0
    }

    return getTimeComparison(a.nextMeeting, b.nextMeeting, aPriority)
  })
}

// Meeting text formatters
const formatMeetingText = (startMoment: moment.Moment, locale: string): string => {
  const today = moment().startOf('day')
  const tomorrow = today.clone().add(1, 'day')
  const yesterday = today.clone().subtract(1, 'day')

  if (startMoment.isSame(today, 'day')) {
    return i18n.t('meetings.one_on_ones.overview_table.time.today_at', {
      time: formatTimeToString(startMoment.toISOString(), locale),
    })
  }
  if (startMoment.isSame(tomorrow, 'day')) {
    return i18n.t('meetings.one_on_ones.overview_table.time.tomorrow_at', {
      time: formatTimeToString(startMoment.toISOString(), locale),
    })
  }
  if (startMoment.isSame(yesterday, 'day')) {
    return i18n.t('meetings.one_on_ones.overview_table.time.yesterday_at', {
      time: formatTimeToString(startMoment.toISOString(), locale),
    })
  }
  return formatDateToString(startMoment.toISOString(), locale)
}

export const createMeetingInfo = (
  meeting: ScheduledMeeting,
  startMoment: moment.Moment,
  locale: string,
  status: 'positive' | 'neutral' | 'info'
): MeetingInfo => ({
  text: formatMeetingText(startMoment, locale),
  status,
  meetingId: meeting.id,
  startDate: meeting.startTime,
  endDate: meeting.endTime,
  templateID: meeting.template?.id ? meeting.template.id : null,
})

interface getNextClosestMeetingResult {
  future: { meeting: ScheduledMeeting; time: moment.Moment } | null
  past: { meeting: ScheduledMeeting; time: moment.Moment } | null
}

export const getNextClosestMeeting = (
  scheduledMeetings: ScheduledMeeting[] | undefined
): getNextClosestMeetingResult => {
  if (!scheduledMeetings?.length) return { future: null, past: null }

  const now = moment()
  let closestFuture: { meeting: ScheduledMeeting; time: moment.Moment } | null = null
  let closestPast: { meeting: ScheduledMeeting; time: moment.Moment } | null = null

  // Single pass to find closest meetings
  for (const meeting of scheduledMeetings) {
    const startMoment = moment(meeting.startTime)
    const endMoment = moment(meeting.endTime)

    // A meeting is considered future if it hasn't ended yet (either hasn't started or is in progress)
    const isFuture = endMoment.isAfter(now)

    if (isFuture) {
      if (!closestFuture || startMoment.isBefore(closestFuture.time)) {
        closestFuture = { meeting, time: startMoment }
      }
    } else {
      // Meeting has already ended, so it's in the past
      if (!closestPast || startMoment.isAfter(closestPast.time)) {
        closestPast = { meeting, time: startMoment }
      }
    }
  }
  return {
    future: closestFuture,
    past: closestPast,
  }
}

const createStatusTag = (status: 'positive' | 'info' | 'neutral' | 'warning', label: string) => ({
  type: 'status' as const,
  value: { status, label },
})

export const getTodayMeetingTag = (nextMeeting: MeetingInfo | null) => {
  if (!nextMeeting)
    return createStatusTag(
      'neutral',
      i18n.t('meetings.one_on_ones.overview_table.time.not_scheduled')
    )

  const now = moment()
  const startMoment = moment(nextMeeting.startDate)
  const endMoment = moment(nextMeeting.endDate)

  const timeUntilMeeting = startMoment.diff(now, 'minutes')
  const isMeetingInProgress = now.isBetween(startMoment, endMoment)

  // Meeting is in progress
  if (isMeetingInProgress) {
    return createStatusTag(
      'warning',
      i18n.t('meetings.one_on_ones.overview_table.time.in_progress')
    )
  }

  // Meeting starts within the next hour
  if (timeUntilMeeting > 0 && timeUntilMeeting <= 60) {
    return createStatusTag(
      'info',
      i18n.t('meetings.one_on_ones.overview_table.time.starts_in', { minutes: timeUntilMeeting })
    )
  }

  if (startMoment.isBefore(now)) {
    return createStatusTag(
      'neutral',
      i18n.t('meetings.one_on_ones.overview_table.time.not_scheduled')
    )
  }

  return createStatusTag('positive', i18n.t('meetings.one_on_ones.overview_table.time.scheduled'))
}
